/**
 * The one error shape a page receives from the plugin. Electron flattens a
 * thrown error to its message on the way through IPC, and again through the
 * context bridge to the page, so the main process answers each call with a
 * result object instead, the preload passes it through as it is, and the
 * page rebuilds a `LocalAgentError` from it (`unwrapOperationResult`).
 * The `/code` client imports this module to tell a refusal from a server
 * that is not running from a failure.
 */

/**
 * The kinds, in one list the type and the guard both come from: `refused` (the
 * client or state refusal, which may follow a server response, such as a
 * permission request no longer being pending), `server-not-running`
 * (`start()` first), `http` (cubic answered the plugin's launch request with
 * the failure `status`), `install` (the plugin could not install OpenCode, or
 * refused the release the launch named) and `failed` (anything else: launch
 * fetch, file system, a crash). A page reads a kind it does not know, from a
 * later plugin, as `failed` (`local-errors.ts`).
 */
export const LOCAL_AGENT_ERROR_KINDS = [
  'refused',
  'server-not-running',
  'http',
  'install',
  'failed',
] as const;

export type LocalAgentErrorKind = (typeof LOCAL_AGENT_ERROR_KINDS)[number];

export type LocalAgentErrorShape = {
  kind: LocalAgentErrorKind;
  message: string;
  /** The server's status for `http` errors. */
  status?: number;
};

export class LocalAgentError extends Error {
  readonly kind: LocalAgentErrorKind;
  readonly status: number | undefined;

  constructor(shape: LocalAgentErrorShape) {
    super(shape.message);
    this.name = 'LocalAgentError';
    this.kind = shape.kind;
    this.status = shape.status;
  }

  toShape(): LocalAgentErrorShape {
    return {
      kind: this.kind,
      message: this.message,
      ...(this.status === undefined ? {} : { status: this.status }),
    };
  }
}

/**
 * What an operation answers over IPC and what `window.todesktop.cubicLocal`
 * resolves with: the operation's value, or the error shape. A page that wants
 * a rejection calls `unwrapOperationResult`.
 */
export type OperationResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: LocalAgentErrorShape };

export function isOperationResult(value: unknown): value is OperationResult<unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const result = value as { ok?: unknown; error?: unknown };
  // A void operation still carries `value: undefined`; a reply without the property is malformed.
  return (
    (result.ok === true && 'value' in result) ||
    (result.ok === false && isLocalAgentErrorShape(result.error))
  );
}

/** The operation's value, or a thrown `LocalAgentError` built from the shape. */
export function unwrapOperationResult<T>(result: OperationResult<T>): T {
  if (result.ok) {
    return result.value;
  }
  throw new LocalAgentError(result.error);
}

export function isLocalAgentErrorShape(value: unknown): value is LocalAgentErrorShape {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const shape = value as Record<string, unknown>;
  return (
    typeof shape.message === 'string' &&
    (shape.status === undefined || typeof shape.status === 'number') &&
    (LOCAL_AGENT_ERROR_KINDS as readonly unknown[]).includes(shape.kind)
  );
}

/**
 * The shape for any error a plugin operation threw: a `LocalAgentError`'s
 * own, and `failed` with the message for anything else.
 */
export function toErrorShape(error: unknown): LocalAgentErrorShape {
  if (error instanceof LocalAgentError) {
    return error.toShape();
  }
  return { kind: 'failed', message: error instanceof Error ? error.message : String(error) };
}
